/**
 * PageHeader — canonical page-title block.
 *
 * Mockup parity: every page in `reignara_redesign/app/dashboard/page.tsx`
 * uses the same three-part header — eyebrow span (uppercase, tracking,
 * muted) → display heading (Bavex, foreground, NOT italic) → optional
 * muted body line. Pre-(m.6) every page hand-rolled this trio with
 * varying classes; many wrongly applied `italic` to the H1.
 *
 * `<PageHeader>` is the single way to express this composite. The
 * heading element is forced to render WITHOUT italic — consumers
 * cannot opt in to italic on a page heading at the type level (no
 * `italic` prop is exposed). KPI numerics use `<KpiTile>` /
 * `text-kpi*` instead. This is the load-bearing enforcement of the
 * (m.6) italic-discipline rule.
 *
 * Sizes
 * -----
 *   - `sm`  : 24px Bavex, used for sub-page titles and modal headings.
 *   - `md`  : 30px Bavex (default), used for primary page titles in
 *             dashboards / portal screens.
 *   - `lg`  : 40px Bavex, used for marketing surfaces (hero blocks).
 *
 * Spec: meridian-redesign — PR (m.6) Phase 1
 * Requirements: 1.5, 14.1, 18.5
 */

import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export interface PageHeaderProps {
  /** Optional eyebrow (uppercase, tracking, muted-foreground) above the title. */
  eyebrow?: string;
  /** Page title. Always rendered with `font-display` and never italic. */
  children: ReactNode;
  /** Optional muted body line below the title. */
  body?: ReactNode;
  /** Optional trailing action slot (right-aligned on `sm` and up). */
  action?: ReactNode;
  /** Title size — defaults to `lg`. */
  size?: 'sm' | 'md' | 'lg';
  /** Heading level for the title element. Defaults to `h1`. */
  as?: 'h1' | 'h2';
  /** Extra classes merged via `cn()`. */
  className?: string;
}

export function PageHeader({
  eyebrow,
  children,
  body,
  action,
  size = 'lg',
  as = 'h1',
  className,
}: PageHeaderProps): JSX.Element {
  const Title = as;
  const titleSize =
    size === 'lg'
      ? 'text-page-title-lg'
      : size === 'sm'
        ? 'text-page-title'
        : 'text-page-title';

  return (
    <header
      data-testid="page-header"
      data-page-header-size={size}
      className={cn(
        'flex flex-col gap-1 sm:flex-row sm:items-end sm:justify-between sm:gap-6',
        'hidden md:flex',
        className,
      )}
    >
      <div className="min-w-0 flex-1">
        {eyebrow ? (
          <span
            data-testid="page-header-eyebrow"
            className="text-eyebrow-sm text-muted-foreground mb-2 block"
          >
            {eyebrow}
          </span>
        ) : null}
        <Title
          data-testid="page-header-title"
          className={cn(
            'font-display text-foreground',
            titleSize,
            // Italic is intentionally excluded — see header docblock.
            // The `not-italic` class is added defensively in case a
            // parent style cascade tries to apply italic.
            'not-italic',
            'break-words',
          )}
        >
          {children}
        </Title>
        {body ? (
          <p
            data-testid="page-header-body"
            className="font-sans text-body text-muted-foreground mt-2 max-w-prose"
          >
            {body}
          </p>
        ) : null}
      </div>
      {action ? (
        <div data-testid="page-header-action" className="shrink-0">
          {action}
        </div>
      ) : null}
    </header>
  );
}

export default PageHeader;
